import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import {
  BarChart3, RefreshCw, Loader2, TrendingUp, Landmark, Leaf, Download, FileSpreadsheet,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/finance/reports`;

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((r) => r?.msg || JSON.stringify(r)).join('; ');
  if (typeof d === 'string') return d;
  return e?.message || '';
};

const csvEscape = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (rows, filename) => {
  const csv = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const GovFinancialReports = () => {
  const { user, loading: authLoading } = useAuth();
  const role = user?.role;
  const allowed = role === 'uske_finance_officer' || role === 'super_admin' || role === 'admin';
  const currentYear = new Date().getUTCFullYear();
  const [year, setYear] = useState(currentYear);
  const [period, setPeriod] = useState('year'); // year | quarter | month
  const [quarter, setQuarter] = useState(Math.ceil((new Date().getUTCMonth() + 1) / 3));
  const [month, setMonth] = useState(new Date().getUTCMonth() + 1);
  const [tab, setTab] = useState('financial'); // financial | des

  const [fin, setFin] = useState(null);
  const [des, setDes] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadFinancial = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year };
      if (period === 'quarter') params.quarter = quarter;
      else if (period === 'month') params.month = month;
      const r = await axios.get(`${API}/financial`, { params });
      setFin(r.data);
    } catch (e) { toast({ title: 'Financial report failed', description: errMsg(e), variant: 'destructive' }); setFin(null); }
    finally { setLoading(false); }
  }, [year, period, quarter, month]);

  const loadDes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/des-quarterly`, { params: { year, quarter } });
      setDes(r.data);
    } catch (e) { toast({ title: 'DES report failed', description: errMsg(e), variant: 'destructive' }); setDes(null); }
    finally { setLoading(false); }
  }, [year, quarter]);

  useEffect(() => { if (tab === 'financial') loadFinancial(); else loadDes(); }, [tab, loadFinancial, loadDes]);

  const years = useMemo(() => {
    const arr = []; for (let y = currentYear - 3; y <= currentYear + 1; y++) arr.push(y); return arr;
  }, [currentYear]);

  if (authLoading) return null;
  if (!allowed) return <Navigate to="/portal/dashboard" replace />;

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const exportFinancialCsv = () => {
    if (!fin) return;
    const rows = [];
    rows.push(['USKE Financial Report', fin.label]);
    rows.push(['Invoice count', fin.totals.invoice_count]);
    rows.push(['Revenue Billed', fin.totals.revenue_billed]);
    rows.push(['Revenue Paid', fin.totals.revenue_paid]);
    rows.push(['Outstanding', fin.totals.outstanding]);
    rows.push(['Sales Tax', fin.totals.sales_tax]);
    rows.push(['Green Revenue', fin.totals.green_revenue]);
    rows.push(['Green %', `${fin.totals.green_pct}%`]);
    rows.push([]);
    rows.push(['By Month', 'Billed', 'Paid', 'Tax', 'Green']);
    fin.by_month.forEach((m) => rows.push([m.label, m.billed, m.paid, m.tax, m.green]));
    rows.push([]);
    rows.push(['By Agency', 'Invoices', 'Billed', 'Paid', 'Tax', 'Green']);
    fin.by_agency.forEach((a) => rows.push([a.agency_name, a.invoice_count, a.billed, a.paid, a.tax, a.green]));
    rows.push([]);
    rows.push(['By Category', 'Lines', 'Billed', 'Green']);
    fin.by_category.forEach((c) => rows.push([c.category, c.line_count, c.billed, c.green]));
    downloadCsv(rows, `USKE-Financial-${fin.label.replace(/\s+/g, '_')}.csv`);
  };

  const exportDesCsv = () => {
    if (!des) return;
    const rows = [];
    rows.push([`WA DES Contract ${des.contract_no} — Quarterly Report`]);
    rows.push(['Contract Holder', des.contract_holder]);
    rows.push(['Reporting Period', des.reporting_period]);
    rows.push([]);
    rows.push(['Total Sales', des.summary.total_sales]);
    rows.push(['Subcontracted Sales', des.summary.subcontracted_sales]);
    rows.push(['MWBE Sales', des.summary.mwbe_sales]);
    rows.push(['SBE Sales', des.summary.sbe_sales]);
    rows.push(['Green Sales', des.summary.green_sales]);
    rows.push(['Agency Count', des.summary.agency_count]);
    rows.push([]);
    rows.push(['Agency', 'Customer Type', 'Invoices', 'Total', 'Green']);
    des.by_agency.forEach((r) => rows.push([r.agency_name, r.customer_type, r.invoice_count, r.total, r.green]));
    downloadCsv(rows, `USKE-DES-${des.reporting_period.replace(/\s+/g, '_')}.csv`);
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4" data-testid="financial-reports-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg"><BarChart3 className="h-6 w-6 text-blue-700" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Financial Reports</h1>
          <p className="text-sm text-slate-500">Revenue, sales tax, category & agency breakdowns, and WA DES quarterly view.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button onClick={() => setTab('financial')} data-testid="fr-tab-financial"
          className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${tab === 'financial' ? 'border-blue-700 text-blue-700 font-medium' : 'border-transparent text-slate-500'}`}>
          Financial Overview
        </button>
        <button onClick={() => setTab('des')} data-testid="fr-tab-des"
          className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${tab === 'des' ? 'border-blue-700 text-blue-700 font-medium' : 'border-transparent text-slate-500'}`}>
          DES Quarterly (Contract 09925)
        </button>
      </div>

      {/* Controls */}
      <Card className="border-2">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Year</Label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border rounded px-2 py-1.5 text-sm bg-white mt-1" data-testid="fr-year">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {tab === 'financial' && (
            <>
              <div>
                <Label className="text-xs">Period</Label>
                <div className="flex gap-1 mt-1">
                  {['year', 'quarter', 'month'].map((p) => (
                    <Button key={p} size="sm" variant={period === p ? 'default' : 'outline'} onClick={() => setPeriod(p)}
                      className={period === p ? 'bg-blue-700 hover:bg-blue-800 text-white' : ''}
                      data-testid={`fr-period-${p}`}>{p[0].toUpperCase() + p.slice(1)}</Button>
                  ))}
                </div>
              </div>
              {period === 'quarter' && (
                <div>
                  <Label className="text-xs">Quarter</Label>
                  <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} className="border rounded px-2 py-1.5 text-sm bg-white mt-1" data-testid="fr-quarter">
                    {[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
                  </select>
                </div>
              )}
              {period === 'month' && (
                <div>
                  <Label className="text-xs">Month</Label>
                  <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="border rounded px-2 py-1.5 text-sm bg-white mt-1" data-testid="fr-month">
                    {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </div>
              )}
            </>
          )}
          {tab === 'des' && (
            <div>
              <Label className="text-xs">Quarter</Label>
              <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} className="border rounded px-2 py-1.5 text-sm bg-white mt-1" data-testid="fr-des-quarter">
                {[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
              </select>
            </div>
          )}
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={tab === 'financial' ? loadFinancial : loadDes} disabled={loading} data-testid="fr-refresh">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm" onClick={tab === 'financial' ? exportFinancialCsv : exportDesCsv} disabled={loading || (tab === 'financial' ? !fin : !des)} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="fr-export-csv">
              <FileSpreadsheet className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
      ) : tab === 'financial' && fin ? (
        <FinancialPanel fin={fin} money={money} />
      ) : tab === 'des' && des ? (
        <DesPanel des={des} money={money} />
      ) : (
        <div className="text-slate-400 text-sm">No data.</div>
      )}
    </div>
  );
};

const KpiCard = ({ label, value, hint, tone = '', testId, icon: Icon }) => (
  <Card className={`border-2 ${tone}`}>
    <CardContent className="p-4">
      <div className="text-xs uppercase text-slate-500 flex items-center gap-1">{Icon && <Icon className="h-3.5 w-3.5" />} {label}</div>
      <div className="text-2xl mt-1 tabular-nums font-bold text-slate-800" data-testid={testId}>{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </CardContent>
  </Card>
);

const FinancialPanel = ({ fin, money }) => {
  const maxBilled = Math.max(1, ...fin.by_month.map((m) => m.billed));
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Revenue Billed" value={money(fin.totals.revenue_billed)} hint={`${fin.totals.invoice_count} invoice(s)`} testId="kpi-revenue-billed" icon={TrendingUp} tone="border-blue-200" />
        <KpiCard label="Revenue Paid"   value={money(fin.totals.revenue_paid)}   hint={`Outstanding ${money(fin.totals.outstanding)}`} testId="kpi-revenue-paid" icon={Landmark} tone="border-emerald-200" />
        <KpiCard label="Sales Tax"      value={money(fin.totals.sales_tax)}      hint="Collected in period" testId="kpi-sales-tax" tone="border-indigo-200" />
        <KpiCard label="Green Revenue"  value={money(fin.totals.green_revenue)}  hint={`${fin.totals.green_pct}% of billed`} testId="kpi-green-revenue" icon={Leaf} tone="border-green-300 bg-green-50/40" />
      </div>

      {/* Monthly bar chart (pure CSS) */}
      <Card className="border-2">
        <CardHeader className="pb-2"><CardTitle className="text-base">Monthly Revenue — {fin.year}</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-12 gap-1 items-end h-40" data-testid="fr-monthly-chart">
            {fin.by_month.map((m) => (
              <div key={m.month} className="flex flex-col items-center h-full">
                <div className="flex-1 flex flex-col justify-end w-full">
                  <div className="w-full bg-blue-600 rounded-t" title={`Billed: ${money(m.billed)}`}
                       style={{ height: `${Math.round((m.billed / maxBilled) * 100)}%`, minHeight: m.billed > 0 ? 2 : 0 }} />
                </div>
                <div className="text-[10px] text-slate-500 mt-1">{m.label}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* By Agency */}
      <Card className="border-2">
        <CardHeader className="pb-2"><CardTitle className="text-base">Revenue by Agency</CardTitle></CardHeader>
        <CardContent>
          {fin.by_agency.length === 0 ? <div className="text-slate-400 text-sm py-2">No sales in period.</div> : (
            <Table headers={['Agency', 'Invoices', 'Billed', 'Paid', 'Tax', 'Green']}>
              {fin.by_agency.map((r) => (
                <tr key={r.agency_id} className="border-t" data-testid={`fr-agency-${r.agency_id}`}>
                  <td className="p-2 font-medium">{r.agency_name}</td>
                  <td className="p-2 tabular-nums">{r.invoice_count}</td>
                  <td className="p-2 tabular-nums font-semibold">{money(r.billed)}</td>
                  <td className="p-2 tabular-nums text-emerald-700">{money(r.paid)}</td>
                  <td className="p-2 tabular-nums text-indigo-700">{money(r.tax)}</td>
                  <td className="p-2 tabular-nums text-green-700">{money(r.green)}</td>
                </tr>
              ))}
            </Table>
          )}
        </CardContent>
      </Card>

      {/* By Category */}
      <Card className="border-2">
        <CardHeader className="pb-2"><CardTitle className="text-base">Revenue by Product Category</CardTitle></CardHeader>
        <CardContent>
          {fin.by_category.length === 0 ? <div className="text-slate-400 text-sm py-2">No sales in period.</div> : (
            <Table headers={['Category', 'Lines', 'Billed', 'Green']}>
              {fin.by_category.map((r) => (
                <tr key={r.category} className="border-t" data-testid={`fr-cat-${r.category}`}>
                  <td className="p-2 font-medium capitalize">{r.category.replace('-', ' ')}</td>
                  <td className="p-2 tabular-nums">{r.line_count}</td>
                  <td className="p-2 tabular-nums font-semibold">{money(r.billed)}</td>
                  <td className="p-2 tabular-nums text-green-700">{money(r.green)}</td>
                </tr>
              ))}
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
};

const DesPanel = ({ des, money }) => (
  <>
    <Card className="border-2 bg-slate-50">
      <CardContent className="p-4">
        <div className="text-xs uppercase text-slate-500">Washington State DES Master Contract</div>
        <div className="text-lg font-semibold text-slate-800 mt-0.5">{des.contract_holder} · Contract #{des.contract_no}</div>
        <div className="text-sm text-slate-600 mt-0.5">Reporting Period: <b>{des.reporting_period}</b></div>
      </CardContent>
    </Card>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <KpiCard label="Total Sales"          value={money(des.summary.total_sales)}          testId="des-total-sales" tone="border-blue-200" />
      <KpiCard label="Subcontracted"        value={money(des.summary.subcontracted_sales)} hint="—" testId="des-subcontracted" tone="border-slate-200" />
      <KpiCard label="MWBE Sales"           value={money(des.summary.mwbe_sales)}           hint="—" testId="des-mwbe" tone="border-slate-200" />
      <KpiCard label="Green / Eco Sales"    value={money(des.summary.green_sales)}          testId="des-green" icon={Leaf} tone="border-green-300 bg-green-50/40" />
    </div>

    <Card className="border-2">
      <CardHeader className="pb-2"><CardTitle className="text-base">Sales by Agency · {des.summary.agency_count} agenc{des.summary.agency_count === 1 ? 'y' : 'ies'}</CardTitle></CardHeader>
      <CardContent>
        {des.by_agency.length === 0 ? <div className="text-slate-400 text-sm py-2">No sales in this quarter.</div> : (
          <Table headers={['Agency', 'Customer Type', 'Invoices', 'Total', 'Green']}>
            {des.by_agency.map((r) => (
              <tr key={r.agency_id} className="border-t" data-testid={`des-row-${r.agency_id}`}>
                <td className="p-2 font-medium">{r.agency_name}</td>
                <td className="p-2 text-xs capitalize text-slate-500">{(r.customer_type || 'public_entity').replace('_', ' ')}</td>
                <td className="p-2 tabular-nums">{r.invoice_count}</td>
                <td className="p-2 tabular-nums font-semibold">{money(r.total)}</td>
                <td className="p-2 tabular-nums text-green-700">{money(r.green)}</td>
              </tr>
            ))}
          </Table>
        )}
      </CardContent>
    </Card>

    <div className="text-xs text-slate-400">
      DES quarterly reports for WA Master Contract 09925 are due within 30 days of the quarter close.
      Export CSV above to file with the contract manager.
    </div>
  </>
);

const Table = ({ headers, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
        <tr>{headers.map((h) => <th key={h} className="text-left p-2">{h}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export default GovFinancialReports;
